import { useEffect, useMemo, useState } from "react";
import { Box, Stack, Tab, useTheme } from "@mui/material";
import { TabContext } from "@mui/lab";
import AutoStrategyConfirmPopup from "./components/AutoStrategyConfirmPopup";
import { useDispatch, useSelector } from "react-redux";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { Strategy } from "../../store/slices/strategySlice";
import {
  AccountDetailInfo,
  Bot,
  TradingViewLaunchProps,
  Notification,
  NotificationResponse,
} from "../../_app/types";
import { useAppNavigate } from "../../_hooks/use-app-navigate";
import { useApi } from "../../_hooks/use-api";
import { RootState } from "../../store/store";
import { ChartingLibraryWidgetOptions } from "../../charting_library/charting_library";
import { HubError } from "../../class/Error";
import { showNotification } from "../../store/slices/notificationSlice";
import binanceAPI from "../../class/binanceApi";
import BybitAPI from "../../class/bybit/bybitApi";
import BadgePaper from "../../_components/BadgePaper";
import { calcRoi, formatCurrency } from "../../utils/utils";
import formatNumber from "../../_utils/formatNumber";
import BadgeDirection from "../../_components/BadgeDirection";
import AutoStrategyMainInfo from "./components/AutoStrategyMainInfo";
import AutoStrategyDetailsInfo from "./components/AutoStrategyDetailsInfo";
import { TradingView } from "../../components/ui/TradingView";
import BotLogs from "../../_components/BotLogs/BotLogs";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import BottomButton from "../../_components/BottomButton";
import { UnmountTabPanel } from "../../_components/UnmountTabPanel";
import SkeletonDetails from "../../_components/SkeletonDetails";
import { TabsStyledV2 } from "../../_components/TabsStyledV2";
import { useParams } from "react-router";
import Text from "../../_components/Text";
import AutoStrategyPerformanceInfo from "./components/AutoStrategyPerformanceInfo";
import EmptyState from "../../_components/EmptyState/EmptyState";
import FuturesBotCard from "../FuturesBotListPage/components/FuturesBotCard";

const AutoStrategyDetailsPage = () => {
  const theme = useTheme();

  const { id: strategyParamsId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const api = useApi();

  const user = useSelector((state: RootState) => state.user);

  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [isLoadingStrategy, setIsLoadingStrategy] = useState<boolean>(true);

  const [bots, setBots] = useState<Bot[]>([]);
  const [isLoadingBots, setIsLoadingBots] = useState<boolean>(true);

  const [logs, setLogs] = useState<Notification[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(true);

  const [account, setAccount] = useState<AccountDetailInfo | null>(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState<boolean>(true);

  const [tickerStr, setTickerStr] = useState<string | null>(null);
  const [isOpenClosePopup, setIsOpenClosePopup] = useState(false);
  const [isDetailsTabView, setIsDetailsTabView] = useState<string>("0");

  const [tradingViewProperties, setTradingViewProperties] = useState<TradingViewLaunchProps>({
    locale: "en",
    debug: false,
    symbol: "",
    interval: "15" as ChartingLibraryWidgetOptions["interval"],
    theme: theme.palette.mode,
    timezone: Intl.DateTimeFormat().resolvedOptions()
      .timeZone as ChartingLibraryWidgetOptions["timezone"],
    autosize: true,
    datafeed: null,
    loading_screen: {
      backgroundColor: theme.myTheme.colors.gray500,
    },
  });

  const isLoading =
    isLoadingBots || isLoadingLogs || isLoadingAccount || isLoadingStrategy ? true : false;

  const totalPnl = useMemo(() => {
    if (!strategy?.totalRealizedPnl || !strategy?.totalUnrealizedPnl) return 0;

    return strategy.totalRealizedPnl + strategy.totalUnrealizedPnl;
  }, [strategy?.totalRealizedPnl, strategy?.totalUnrealizedPnl]);

  const fetchStrategy = async () => {
    if (!strategyParamsId) return;

    try {
      const response = await api.getStrategy(strategyParamsId);
      setStrategy(response);
      return response;
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingStrategy(false);
    }
  };

  const getBots = async () => {
    if (!strategyParamsId) return;

    try {
      const response = await api.getBotsStrategy(strategyParamsId);
      console.log(response);
      setBots(response.bots);
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingBots(false);
    }
  };

  const getLogs = async () => {
    if (!strategyParamsId) return;

    try {
      const response: NotificationResponse = await api.getStrategyLogs(strategyParamsId);
      setLogs(response.content);
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const getAccount = async () => {
    if (!strategy) return;

    try {
      const response = await api.getAccountInformation(strategy.accountId);
      setAccount(response);
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingAccount(false);
    }
  };

  useEffect(() => {
    if (!strategyParamsId) return;

    fetchStrategy();
    getLogs();
  }, []);

  useEffect(() => {
    if (!user.token || !strategy) return;

    const controller = new AbortController();
    const { signal } = controller;

    if (strategy.status === "RUNNING") {
      const fetchData = async () => {
        await fetchEventSource(
          `${process.env.REACT_APP_API_NOTIFICATION}/api/v1/strategy-notifications?strategyId=${strategy.id}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
            onmessage(event: any) {
              try {
                const parsedData = JSON.parse(event.data) as Notification;
                setLogs((prevMessages: Notification[]) => [...prevMessages, parsedData]);
              } catch (error) {
                console.error("useSse parsing error");
              }
            },
            signal,
          }
        );
      };
      fetchData();
    }

    return () => controller.abort();
  }, [strategyParamsId, strategy?.status, user.token]);

  useEffect(() => {
    if (!strategy?.accountId) return;

    getAccount();
  }, [strategy?.accountId]);

  useEffect(() => {
    if (!strategy?.id) return;

    getBots();
  }, [strategy?.id]);

  useEffect(() => {
    if (!strategy?.base || !strategy?.quote) return;

    setTickerStr(strategy.base + strategy.quote);
  }, [strategy?.base, strategy?.quote]);

  useEffect(() => {
    if (!tickerStr || !account?.market) return;

    const datafeed =
      account.market === "BINANCE"
        ? new binanceAPI({ debug: false, ticker: tickerStr })
        : new BybitAPI({ debug: false, ticker: tickerStr });

    setTradingViewProperties((prevState) => ({
      ...prevState,
      symbol: tickerStr,
      datafeed: datafeed,
    }));
  }, [strategy?.base, account?.market]);

  useEffect(() => {
    if (!strategy?.id) return;

    const interval = setInterval(fetchStrategy, 3000);
    return () => clearInterval(interval);
  }, [strategy?.id]);

  return (
    <PageLayout
      header={<Header pageTitle={`AUTOBOT ${strategy?.displayedId || ""}`} needBack />}
      bottomButton={
        !isLoading &&
        strategy &&
        (["SHUTDOWN", "UNSUCCESSFUL_START"].includes(strategy.status ?? "") ? (
          <BottomButton label="Create new strategy" onClick={() => navigate.createStrategy()} />
        ) : (
          <BottomButton
            disabled={strategy.status === "CLOSING" || strategy.status === "WAITING_TO_BE_CLOSED"}
            label="Close strategy"
            onClick={() => setIsOpenClosePopup(true)}
          />
        ))
      }
      contentSx={{ paddingTop: "20px" }}
    >
      {isLoading ? (
        <SkeletonDetails tabsCount={3} />
      ) : strategy ? (
        <Box display="grid" gridTemplateRows="auto 1fr" height="100%">
          <Stack paddingX="8px" direction="row" alignItems="center" gap="8px" mb="22px">
            <BadgePaper
              sx={{ flexGrow: 1, height: "78px" }}
              label="Grid profit"
              value={formatCurrency(formatNumber(strategy.gridProfit), strategy.quote)}
            />
            <BadgePaper
              sx={{ flexGrow: 1, height: "77px" }}
              label="Total profit"
              value={formatCurrency(formatNumber(totalPnl), strategy.quote)}
            />
            <BadgePaper
              sx={{ flexGrow: 1, height: "77px" }}
              label="Total ROI"
              value={`${formatNumber(calcRoi(totalPnl, strategy.investmentQuote, strategy.leverage) * 100)}%`}
            />
          </Stack>

          <Box
            sx={{
              backgroundColor: theme.myTheme.colors.gray600,
              borderTopRightRadius: "16px",
              borderTopLeftRadius: "16px",
            }}
          >
            <TabContext value={isDetailsTabView}>
              <TabsStyledV2
                onChange={(_, value) => setIsDetailsTabView(value)}
                sx={{
                  position: "sticky",
                  top: "-21px",
                  zIndex: 2000,
                  backgroundColor: theme.myTheme.colors.gray600,
                }}
              >
                <Tab
                  sx={{
                    borderTopLeftRadius: "0px !important",
                  }}
                  label="Details"
                  value="0"
                />
                <Tab label="Bots" value="1" />
                <Tab
                  sx={{
                    borderTopRightRadius: "0px !important",
                  }}
                  label="Logs"
                  value="2"
                />
              </TabsStyledV2>

              <UnmountTabPanel
                noPadding
                value="0"
                sx={{
                  background: theme.myTheme.colors.gray500,
                  height: "calc(100% - 35px + 119px)",
                }}
              >
                <Stack alignItems="center" direction={"row"} gap="8px" mb="12px" pl="16px">
                  <Text variant="regular-semi" color={theme.myTheme.colors.foreground100}>
                    {`${strategy.base}/${strategy.quote}`}
                  </Text>
                  <BadgeDirection gridDirection={strategy.gridDirection} />
                </Stack>

                {account?.market && tradingViewProperties.datafeed && (
                  <Box paddingX="5px" position="relative" zIndex="1000">
                    <TradingView
                      background={theme.myTheme.colors.gray500}
                      ChartProperties={tradingViewProperties}
                    />
                  </Box>
                )}

                <Box paddingX="16px">
                  <Stack direction="column" alignItems="center" gap="32px" marginTop="32px">
                    <AutoStrategyMainInfo strategy={strategy} botsCount={bots ? bots.length : 0} />
                    <AutoStrategyPerformanceInfo strategy={strategy} />
                    <AutoStrategyDetailsInfo
                      strategy={strategy}
                      accountName={account?.name ?? ""}
                    />
                  </Stack>
                </Box>
              </UnmountTabPanel>

              <UnmountTabPanel
                value="1"
                sx={{
                  background: theme.myTheme.colors.gray500,
                  height: "calc(100% - 35px + 119px)",
                }}
              >
                {bots && bots.length > 0 ? (
                  <Stack direction={"column"} spacing={1}>
                    {bots.map((bot, index) => (
                      <FuturesBotCard key={`bot-list-card-${index}`} bot={bot} />
                    ))}
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    mt="-45px"
                  >
                    <EmptyState text="Bots not found" />
                  </Stack>
                )}
              </UnmountTabPanel>

              <UnmountTabPanel
                value="2"
                sx={{
                  background: theme.myTheme.colors.gray500,
                  height: "calc(100% - 35px + 119px)",
                }}
              >
                <BotLogs logs={logs} />
              </UnmountTabPanel>

              {(strategy.status === "RUNNING" || strategy.status === "ERROR") && (
                <AutoStrategyConfirmPopup
                  open={isOpenClosePopup}
                  onClose={setIsOpenClosePopup}
                  id={strategy.id}
                  displayedId={strategy.displayedId}
                />
              )}
            </TabContext>
          </Box>
        </Box>
      ) : null}
    </PageLayout>
  );
};

export default AutoStrategyDetailsPage;
